import React from "react";
const { useEffect: useEffect_AI, useRef: useRef_AI, useState: useState_AI } = React;

/*
 * S04 direct rebuild
 *
 * The three approved art-direction previews remain self-contained.  The
 * parent selector is deliberately the only shared layer: it must not rewrite
 * their stage proportions, animation timing, or mobile composition.
 */
function S04IntegratedTransformationSection() {
  const [activeId, setActiveId] = useState_AI("ai-workflow");
  const [frameHeight, setFrameHeight] = useState_AI(760);
  const refs = useRef_AI([]);
  const frameRef = useRef_AI(null);
  const states = [
    { id: "ai-workflow", label: "AI Business & Workflow", src: "/s04-reference/ax/index.html", title: "AI Business & Workflow preview" },
    { id: "product-mvp", label: "Product / MVP Development", src: "/s04-reference/mvp/index.html", title: "Product / MVP Development preview" },
    { id: "web-system", label: "Web System & App Development", src: "/s04-reference/web/index.html", title: "Web System & App Development preview" },
  ];
  const activeIndex = Math.max(0, states.findIndex((state) => state.id === activeId));
  const active = states[activeIndex];
  const introCopy = active.id === "product-mvp"
    ? <p className="s04-founder-copy"><strong>Founder-led product direction, grounded in Myanmar.</strong>With 12 years of experience living in Myanmar and 20 years in technology, our Japanese CEO works alongside you from the first idea to a product people can choose. We shape the customer problem, product direction, launch approach, and monetisation hypotheses with PMF in mind — so the next build is grounded in the market, not just the brief.</p>
    : <p>Start with the business decision, not a tool. Move from an AI-supported workflow, to a focused product, system, app, or platform when that is the practical next step.</p>;
  const select = (nextIndex, focus = false) => {
    const index = (nextIndex + states.length) % states.length;
    setActiveId(states[index].id);
    if (focus) window.requestAnimationFrame(() => refs.current[index]?.focus());
  };
  const onKeyDown = (event, index) => {
    const keys = { ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: states.length - 1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    select(keys[event.key], true);
  };
  useEffect_AI(() => {
    const setReferenceHeight = (event) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type !== "s04-reference-height") return;
      const next = Math.ceil(Number(event.data.height));
      if (Number.isFinite(next) && next >= 480 && next <= 1800) setFrameHeight(next);
    };
    window.addEventListener("message", setReferenceHeight);
    return () => window.removeEventListener("message", setReferenceHeight);
  }, [activeId]);
  return (
    <section className="section-pad s04-direct-section" id="ai-transformation" aria-labelledby="s04-direct-title">
      <style>{`
        .s04-direct-section{overflow:clip;background:var(--paper-inset)}
        .s04-direct-intro{display:grid;grid-template-columns:1.15fr .65fr;gap:72px;align-items:end;margin:22px 0 38px}
        .s04-direct-intro h2{max-width:820px;margin:0;font-family:var(--font-serif);font-size:clamp(49px,5.3vw,78px);font-weight:500;line-height:.94;letter-spacing:-.067em}
        .s04-direct-intro p{max-width:420px;margin:0 0 4px;color:var(--fg-muted);font-size:16px;line-height:1.58}
        .s04-founder-copy strong{display:block;margin-bottom:10px;color:var(--fg);font-size:17px;line-height:1.28}
        .s04-founder-copy{display:none}
        .s04-direct-tabs{display:flex;gap:8px;align-items:center;margin-bottom:18px;border-bottom:1px solid var(--line)}
        .s04-direct-tabs button{position:relative;padding:13px 2px 14px;border:0;background:transparent;color:var(--fg-subtle);font:650 13px/1.2 var(--font-sans);text-align:left;cursor:pointer}
        .s04-direct-tabs button+button{margin-left:22px}.s04-direct-tabs button[aria-selected="true"]{color:var(--fg)}
        .s04-direct-tabs button[aria-selected="true"]:after{position:absolute;right:0;bottom:-1px;left:0;height:2px;background:var(--indigo);content:""}
        .s04-direct-tabs button:focus-visible{outline:2px solid var(--indigo);outline-offset:4px}
        .s04-direct-frame{width:100%;min-height:620px;border:1px solid var(--line);border-radius:28px;background:#fffefd;box-shadow:0 20px 60px rgba(20,31,57,.045);transition:height .24s ease}
        .s04-direct-frame iframe{display:block;width:100%;height:100%;border:0;border-radius:inherit;background:transparent}
        @media(max-width:760px){.s04-direct-section .container{padding-right:18px;padding-left:18px}.s04-direct-intro{display:block;margin:19px 0 28px}.s04-direct-intro h2{font-size:47px}.s04-direct-intro p{margin-top:19px;font-size:14px}.s04-founder-copy{display:block}.s04-direct-tabs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:4px;margin-bottom:14px}.s04-direct-tabs button,.s04-direct-tabs button+button{min-height:52px;margin:0;padding:8px 7px;border:1px solid var(--line);border-radius:10px;text-align:center;font-size:11px;line-height:1.22}.s04-direct-tabs button[aria-selected="true"]{border-color:#cbd7fb;background:#eff3ff;color:#244dbf}.s04-direct-tabs button[aria-selected="true"]:after{display:none}.s04-direct-frame{min-height:760px;border-radius:22px}}
      `}</style>
      <div className="container">
        <div className="eyebrow">AI TRANSFORMATION TO DIGITAL DELIVERY</div>
        <header className="s04-direct-intro"><h2 id="s04-direct-title">Choose the right change.<br />Build what follows.</h2>{introCopy}</header>
        <nav className="s04-direct-tabs" aria-label="AI transformation service areas" role="tablist">
          {states.map((state, index) => <button key={state.id} type="button" role="tab" aria-selected={state.id === active.id} aria-controls="s04-direct-preview" tabIndex={state.id === active.id ? 0 : -1} ref={(node) => { refs.current[index] = node; }} onClick={() => select(index)} onKeyDown={(event) => onKeyDown(event, index)}>{state.label}</button>)}
        </nav>
        <div className="s04-direct-frame" id="s04-direct-preview" role="tabpanel" aria-label={active.title} style={{ height: `${frameHeight}px` }}>
          <iframe ref={frameRef} key={active.id} src={active.src} title={active.title} loading="eager" />
        </div>
      </div>
    </section>
  );
}

function AIDetail() {
  return <S04IntegratedTransformationSection />;
}

export { AIDetail };
